import i18n from '@/utils/i18n';
import { ItemListOption, ItemListOptionMetadata } from './item-context-types';
import { ListOption } from '@/components/list-item';
import { getItemSubtitle as moneyGetItemSubtitle } from '@/utils/money';

export type ItemListOptionMatrixType = ItemListOption[][];

export type CarouselListOption = ListOption<{
  items: ItemListOption[];
}>;

interface SkuDetails {
  numPacks: number;
  numPrices: number;
  selectedSku?: ItemListOptionMetadata | null;
}

/**
 * Selects an sku from a list of skus. The sku with the lowest price will be selected. If no sku has a price and one has a pack string, that one will be selected.
 * If no prices and not one pack string, no sku is selected
 * @param skus ItemListOptionMetadata[]
 * @returns SkuDetails
 */
export const getSkuDetails = (skus: ItemListOptionMetadata[]): SkuDetails => {
  let numPrices = 0;
  let numPacks = 0;
  let selectedSku = null;
  let packDisplaySku = null;
  for (let i = 0; i < skus.length; i++) {
    const sku = skus[i];
    if (sku.packDisplayString) {
      numPacks++;
      packDisplaySku = sku;
    }
    if (typeof sku.unitPriceInMicros === 'number') {
      numPrices++;
      if (
        !selectedSku ||
        selectedSku.unitPriceInMicros! > sku.unitPriceInMicros
      ) {
        selectedSku = sku;
      }
    }
  }
  // If there is one pack display string and no prices, select that sku
  if (numPacks === 1 && numPrices === 0) {
    selectedSku = packDisplaySku;
  }
  return {
    numPacks,
    numPrices,
    selectedSku,
  };
};

// Converts list of recommendations to array of grouped ItemListOption, i.e. a matrix of columns and rows for use in item recommendation carousel
export const convertRecommendationsToCarouselListOptions = (
  arr: ItemListOption[],
  numRows: number,
): CarouselListOption[] => {
  const cols: CarouselListOption[] = [];
  for (let i = 0; i < arr.length; i += numRows) {
    const col: CarouselListOption = {
      metadata: { items: [] },
      title: '',
      value: '',
    };
    const rows = arr.slice(i, i + numRows);
    rows.map((row, j) => {
      col.metadata!.items.push(row);
      if (j > 0) {
        col.title = `${col.title}_${row.title}`;
        col.value = `${col.value}_${row.value}`;
      } else {
        col.title = row.title;
        col.value = row.value;
      }
    });
    cols.push(col);
  }
  return cols;
};

export const getCarouselItemSubtitle = (
  skus: ItemListOptionMetadata[],
): string => {
  const { selectedSku, numPacks, numPrices } = getSkuDetails(skus);
  const seeOptionsString = i18n.t('see_options');

  // If no selectedSku or no pack units
  if (!selectedSku || numPacks === 0) {
    return seeOptionsString;
  }

  const packDisplayString = selectedSku.packDisplayString.toUpperCase() || '';
  // If no prices and selected
  if (numPrices === 0) {
    // If selectedSku, use its pack string otherwise 'see options'
    return packDisplayString ?? seeOptionsString;
  }

  const displayStringWithPrice = moneyGetItemSubtitle({
    currencyCode: selectedSku.currencyCode,
    displayString: packDisplayString,
    unitCount: selectedSku.unitCount,
    unitPriceInMicros: selectedSku.unitPriceInMicros,
  });

  // If multiple prices
  if (numPrices > 1) {
    return displayStringWithPrice
      ? `${i18n.t('starting_at', { price: displayStringWithPrice })}`
      : seeOptionsString;
  }
  // If single price
  return displayStringWithPrice;
};
